<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { IOnboardingStudent } from '@/interfaces';
import { computed, toRefs } from 'vue';
import UserAvatar from '../UserAvatar.vue';
import { TAvatarSelection } from './UserAvatarSelect.vue';

type TAvatarButtonStudent = Components.Schemas.StudentDto | IOnboardingStudent | TAvatarSelection;

const props = withDefaults(
  defineProps<{
    student?: TAvatarButtonStudent | null;
    avatarName?: string | undefined | null;
    avatarPictureId?: number | undefined | null;
    disabled?: boolean;
    hoverable?: boolean;
    isSelected?: boolean;
    type?: 'button' | 'submit';
  }>(),
  {
    disabled: false,
    hoverable: false,
    type: 'button',
    student: null,
    avatarName: null,
    avatarPictureId: null,
  },
);
const { avatarName, avatarPictureId, disabled, student } = toRefs(props);

const hasAvatarPictureFile = (
  value: TAvatarButtonStudent | null | undefined,
): value is IOnboardingStudent | TAvatarSelection => {
  return value != null && 'avatarPictureFile' in value;
};

const firstName = computed(() => student.value?.firstName || '');
const lastName = computed(() => student.value?.lastName || '');
const resolvedAvatarPictureFile = computed<File | null>(() =>
  hasAvatarPictureFile(student.value) ? (student.value.avatarPictureFile ?? null) : null,
);
const resolvedAvatarPictureId = computed<number | null>(
  () => avatarPictureId.value ?? student.value?.avatarPictureId ?? null,
);
</script>

<template>
  <button
    :type="type"
    :disabled="disabled"
    :class="[
      'flex items-center justify-center shrink-0',
      'outline-inner outline-primary',
      hoverable && 'hover:outline outline-4',
      'size-button rounded-full',
      disabled && 'opacity-50 pointer-events-none',
      isSelected && 'outline outline-4',
    ]"
  >
    <UserAvatar
      :user="{
        avatar: avatarName,
        avatarPictureFile: resolvedAvatarPictureFile,
        avatarPictureId: resolvedAvatarPictureId,
        firstName: firstName,
        lastName: lastName,
      }"
      :class="[
        'size-full outline-white outline-offset-[-2px]',
        isSelected && 'outline outline-[2px]',
        hoverable && 'hover:outline hover:outline-[2px] active:outline active:outline-[2px]',
      ]"
    />
  </button>
</template>
